import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";

import DashboardLayout from "../../Layout/DashboardLayout";
import ConfigTable, { ChipFilterBar } from "../../Common/Tables/ConfigTable";
import {
  Button,
  PageHeading,
  StatusChip,
  Icon,
  ConfirmDialog,
  employeeID,
  companyID,
} from "../../Common/UIComponents";
import {
  fetchSettingsDropdownValues,
  fetchBasicStandards,
  updateActivitySortOrder,
  deleteTwoColumnListEntry,
  fetchSettingsStats,
} from "../../API_Service/Settings_Slice";
import { useGlobalLoader } from "../../Common/GlobalLoader";

// ─────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────

const normalizeTerm = (term = "") =>
  term.toString().trim().replace(/\s+/g, "").toUpperCase();

const toArray = (res) => {
  const d = res?.payload?.data ?? res?.payload ?? res?.data ?? res;
  return Array.isArray(d) ? d : [];
};

const normalizeItem = (item = {}) => ({
  ...item,
  id:    item.id    ?? item.ID    ?? item.code ?? item.CODE ??
         String(item.value ?? item.Value ?? item.label ?? item.Label ?? ""),
  label: String(item.label ?? item.Label ?? item.Value ?? item.value ?? "").trim(),
  value: item.value ?? item.Value ?? item.label ?? item.Label,
});

const buildChipFilters = (items = []) =>
  items.map((item) => ({
    id:    item.id,
    label: item.label ?? item.Label ?? item.Value ?? item.value ?? "Unknown",
    query: String(item.value ?? item.Value ?? item.label ?? item.Label ?? "").toLowerCase(),
  }));

// ── For Activity chip: item may carry a Type field alongside Value ────────────
const buildTableRows = (items = [], typeLabel = "Dropdown", isActivity = false) =>
  items.map((item, i) => ({
    id:         item.id ?? `row-${i}`,
    type:       typeLabel,
    value:      item.label ?? item.Label ?? item.value ?? item.Value ?? "Unknown",
    // Activity-specific: the sub-type stored in item.Type / item.type2 / item.SubType etc.
    activityType: item.Type ?? item.type2 ?? item.SubType ?? item.ActivityType ?? "",
    indicator:  null,
  }));

const IS_ACTIVITY = (chipLabel = "") =>
  chipLabel.trim().toLowerCase() === "activity";

const EXPORT_COLUMNS_DEFAULT  = [
  { key: "type",  title: "Type"  },
  { key: "value", title: "Value" },
];


// ─────────────────────────────────────────────
// DRAGGABLE ACTIVITY TABLE (Activities only)
// ─────────────────────────────────────────────
// ─────────────────────────────────────────────
// PAGE
// ─────────────────────────────────────────────

export default function DropdownConfigIndex({ initialChip = null, embedded = false } = {}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { enableGlobalOverlay, disableGlobalOverlay } = useGlobalLoader();

  const [dropdownValues,   setDropdownValues]   = useState([]);
  const [basicStandards,   setBasicStandards]   = useState([]);
  const [isLoading,        setIsLoading]        = useState(false);
  const [deleteLoading,    setDeleteLoading]    = useState(false);
  const [deleteError,      setDeleteError]      = useState(null);
  const [settingsError,    setSettingsError]    = useState(null);
  const [activeChip,       setActiveChip]       = useState(null);
  const [searchQuery,      setSearchQuery]      = useState("");
  const [currentPage,      setCurrentPage]      = useState(1);
  const [pageSize,         setPageSize]         = useState(10);
  const [userChipSelected, setUserChipSelected] = useState(false);
  const [deleteDialog,     setDeleteDialog]     = useState({ open: false, row: null });
  const [deleteSuccess,    setDeleteSuccess]    = useState(false);
  const [deleteBlocked,    setDeleteBlocked]    = useState(false);
  const [activityRows,     setActivityRows]     = useState([]);
  const [isSavingOrder,    setIsSavingOrder]    = useState(false);

  // ── Derived: is the currently selected chip "Activity"? ──────────────────
  const isActivityChip = useMemo(() => IS_ACTIVITY(activeChip?.label ?? ""), [activeChip]);

  // ── Fetch dropdown types ──────────────────────
  const loadDropdownValues = useCallback((currentActiveChip) => {
    setIsLoading(true);
    setSettingsError(null);
    disableGlobalOverlay();
    dispatch(fetchSettingsDropdownValues())
      .then((res) => {
        const items = toArray(res).map(normalizeItem);
        setDropdownValues(items);
        enableGlobalOverlay();
        if (currentActiveChip) {
          dispatch(fetchBasicStandards({ term: normalizeTerm(currentActiveChip.label), companyID }))
            .then((r) => setBasicStandards(toArray(r).map(normalizeItem)))
            .catch(() => setBasicStandards([]));
        }
      })
      .catch(() => { setSettingsError("Failed to load dropdown values"); enableGlobalOverlay(); })
      .finally(() => setIsLoading(false));
  }, [dispatch, enableGlobalOverlay, disableGlobalOverlay]);

  useEffect(() => {
    dispatch(fetchSettingsStats());
    loadDropdownValues(activeChip);
  }, [location.key]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Fetch values for selected chip ────────────
  const loadBasicStandards = useCallback((chip) => {
    if (!chip) return;
    dispatch(fetchBasicStandards({ term: normalizeTerm(chip.label), companyID }))
      .then((res) => setBasicStandards(toArray(res).map(normalizeItem)))
      .catch(() => setBasicStandards([]));
  }, [dispatch]);

  const chipFilters = useMemo(
    () => (dropdownValues.length ? buildChipFilters(dropdownValues) : []),
    [dropdownValues]
  );

  // ── Auto-select chip ──────────────────────────
  useEffect(() => {
    if (userChipSelected || !chipFilters.length) return;
    if (initialChip) {
      const restored = chipFilters.find((c) => c.id === initialChip.id) ?? initialChip;
      setActiveChip(restored);
      setSearchQuery("");
      setUserChipSelected(true);
      loadBasicStandards(restored);
      return;
    }
    const defaultChip =
      chipFilters.find((c) => c.label.toLowerCase().includes("basic standard")) ??
      chipFilters[0];
    setActiveChip(defaultChip);
    setSearchQuery("");
    loadBasicStandards(defaultChip);
  }, [chipFilters, userChipSelected, initialChip, loadBasicStandards]);

  // ── Rows ──────────────────────────────────────
  const rows = useMemo(() => {
    if (activeChip && basicStandards.length)
      return buildTableRows(basicStandards, activeChip.label, isActivityChip);
    if (dropdownValues.length && !activeChip)
      return buildTableRows(dropdownValues, "Dropdown", false);
    return [];
  }, [activeChip, dropdownValues, basicStandards, isActivityChip]);

  const filteredRows = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      r.type.toLowerCase().includes(q) ||
      r.value.toLowerCase().includes(q) ||
      (r.activityType ?? "").toLowerCase().includes(q)
    );
  }, [searchQuery, rows]);

  const totalPages    = Math.max(1, Math.ceil(filteredRows.length / pageSize));
  const paginatedRows = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredRows.slice(start, start + pageSize);
  }, [filteredRows, currentPage, pageSize]);

  // ── Handlers ─────────────────────────────────
  const handleEdit = useCallback(
    (row) => navigate("/application-config/dropdown/create", {
      state: {
        mode: "edit",
        data: {
          id:           row.id,
          value:        row.value,
          type:         row.type,
          activityType: row.activityType ?? "",
        },
        backgroundLocation: location,
        activeChip,
      },
    }),
    [navigate, location, activeChip]
  );

  const handleDelete       = useCallback((row) => setDeleteDialog({ open: true, row }), []);
  const handleCancelDelete = useCallback(() => { setDeleteDialog({ open: false, row: null }); setDeleteBlocked(false); }, []);

  const handleConfirmDelete = useCallback(() => {
    const { row } = deleteDialog;
    if (!row) return;
    const term = normalizeTerm(activeChip?.label ?? row.type ?? "");
    setDeleteLoading(true);
    setDeleteError(null);
    dispatch(deleteTwoColumnListEntry({ term, id: row.id, modifiedBy: employeeID, companyID }))
      .then((action) => {
        const result = action?.payload?.data;
        if (Number(result) === -1) {
          setDeleteBlocked(true);
          return;
        }
        if (Number(result) > 0) {
          setDeleteBlocked(false);
          setDeleteDialog({ open: false, row: null });
          setDeleteSuccess(true);
          loadBasicStandards(activeChip);
          dispatch(fetchSettingsStats());
          setTimeout(() => setDeleteSuccess(false), 3000);
        } else {
          setDeleteError("Delete failed");
        }
      })
      .catch(() => setDeleteError("Delete failed"))
      .finally(() => setDeleteLoading(false));
  }, [deleteDialog, activeChip, dispatch, loadBasicStandards]);

  const handleAddNew = useCallback(
    () => navigate("/application-config/dropdown/create", {
      state: { backgroundLocation: location, activeChip },
    }),
    [navigate, location, activeChip]
  );

  const handleChipSelect = useCallback((chip) => {
    setActiveChip(chip);
    setSearchQuery("");
    setCurrentPage(1);
    setUserChipSelected(true);
    loadBasicStandards(chip);
  }, [loadBasicStandards]);

  const handleSearchChange   = useCallback((e) => { setSearchQuery(e.target.value); setCurrentPage(1); }, []);
  const handlePageSizeChange = useCallback((size) => { setPageSize(size); setCurrentPage(1); }, []);

  // ── Activity drag-and-drop ────────────────────
  // Keep activityRows in sync whenever the fetched rows change (not during save)
  useEffect(() => {
    if (isActivityChip) setActivityRows(rows);
  }, [rows, isActivityChip]); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredActivityRows = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return activityRows;
    return activityRows.filter(r =>
      r.value.toLowerCase().includes(q) ||
      (r.activityType ?? "").toLowerCase().includes(q)
    );
  }, [activityRows, searchQuery]);

  const activityTotalPages = Math.max(1, Math.ceil(filteredActivityRows.length / pageSize));

  const paginatedActivityRows = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredActivityRows.slice(start, start + pageSize);
  }, [filteredActivityRows, currentPage, pageSize]);

  const saveActivityOrder = useCallback((orderedRows) => {
    setIsSavingOrder(true);
    dispatch(updateActivitySortOrder({
      CompanyID: companyID,
      Items: orderedRows.map((row, i) => ({
        activityID: row.id,
        sortOrder:  i + 1,
      })),
    })).finally(() => {
      setIsSavingOrder(false);
      loadBasicStandards(activeChip);
    });
  }, [dispatch, activeChip, loadBasicStandards]);

  const handleSortEnd = useCallback((reorderedPage) => {
    // Reconstruct the full activityRows list from the page-level reorder
    const newRows = [...activityRows];
    const pageItemIds = paginatedActivityRows.map((r) => r.id);
    const fullIndices = pageItemIds.map((id) => newRows.findIndex((r) => r.id === id));
    reorderedPage.forEach((row, i) => { newRows[fullIndices[i]] = row; });
    setActivityRows(newRows);
    saveActivityOrder(newRows);
  }, [activityRows, paginatedActivityRows, saveActivityOrder]);

  // ── Columns — switch based on chip ────────────
  const columns = useMemo(() => {
    if (isActivityChip) {
      return [
        {
          key: "value",
          title: "Activity",
          render: (row) => (
            <span className="text-sm font-medium text-[#181c1d]">{row.value}</span>
          ),
        },
        {
          key: "activityType",
          title: "Type",
          render: (row) =>
            row.activityType ? (
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-[#08757C]/10 text-[#08757C]">
                {row.activityType}
              </span>
            ) : (
              <span className="text-[#bdc9c9] text-xs">—</span>
            ),
        },
      ];
    }
    return [
      {
        key: "value",
        title: "Value",
        render: (row) =>
          row.indicator ? (
            <StatusChip label={row.value} variant={row.indicator} />
          ) : (
            <span className="text-sm text-[#181c1d] whitespace-pre-wrap">{row.value}</span>
          ),
      },
    ];
  }, [isActivityChip]);

  const inner = (
    <>
      <div className="px-8 py-4 bg-[#f7fafa] text-[#181c1d]">


        <div className="flex flex-wrap justify-between items-end mb-3 gap-4">
          <PageHeading
            title="Dropdown Management"
            subtitle="Define and manage dropdown values used across your laboratory system to ensure consistent data entry and standardized selections."
          />
          <Button variant="primary" onClick={handleAddNew}>
            <Icon name="add" className="mr-1 text-[20px]" />
            Add New Dropdown
            {/* {isActivityChip ? "Add Activity" : "Add New Dropdown"} */}
          </Button>
        </div>

        <div>
          <ChipFilterBar chips={chipFilters} activeChip={activeChip} onChipSelect={handleChipSelect} />

          <ConfigTable
            columns={columns}
            rows={isLoading ? [] : (isActivityChip ? paginatedActivityRows : paginatedRows)}
            onEdit={handleEdit}
            onDelete={handleDelete}
            currentPage={currentPage}
            totalPages={isActivityChip ? activityTotalPages : totalPages}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
            onPageSizeChange={handlePageSizeChange}
            emptyText={isLoading ? "Loading…" : `No ${isActivityChip ? "activities" : "dropdown values"} found.`}
            searchQuery={searchQuery}
            onSearchChange={handleSearchChange}
            allFilteredRows={isActivityChip ? filteredActivityRows : filteredRows}
            exportColumns={EXPORT_COLUMNS_DEFAULT}
            exportFilename={isActivityChip ? "activities" : "dropdown_values"}
            sortable={isActivityChip}
            onSortEnd={handleSortEnd}
            isSavingOrder={isSavingOrder}
            showPagination={true}
          />

          {settingsError && (
            <div className="mt-6 rounded-xl bg-red-50 text-red-700 p-6 text-sm">{settingsError}</div>
          )}
        </div>
      </div>

      <ConfirmDialog
        isOpen={deleteDialog.open}
        variant="danger"
        isLoading={deleteLoading}
        errorMessage={deleteBlocked ? "This item is already mapped with another module. Please remove the mapping before deleting." : null}
        cancelLabel={deleteBlocked ? "OK, Got it" : "Cancel"}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />

      {deleteSuccess && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[100] bg-[#bbf46e] text-[#112000] px-6 py-3 rounded-xl shadow-lg flex items-center gap-2 text-sm font-semibold" role="status">
          <Icon name="check_circle" className="text-[20px]" filled />
          Dropdown Deleted successfully.
        </div>
      )}

      {deleteError && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[100] bg-red-600 text-white text-sm font-semibold px-6 py-3 rounded-xl shadow-lg">
          {deleteError}
        </div>
      )}
    </>
  );
  return embedded ? inner : <DashboardLayout>{inner}</DashboardLayout>;
}
